import { Button } from '@trellis/roots/button'
import { Typography } from '@trellis/roots/typography'
import { Share, X } from 'lucide-react'
import { useState } from 'react'

const DISMISSED_STORAGE_KEY = 'trellis-ios-install-instructions-dismissed'

function isIosSafari(): boolean {
  const userAgent = window.navigator.userAgent
  const isIos =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
  const isSafari = /Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent)
  return isIos && isSafari
}

function isStandalone(): boolean {
  /* iOS Safari exposes install state via the nonstandard navigator.standalone rather than
   * the display-mode media query, so both are checked. */
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true
  )
}

export function IosInstallInstructions() {
  const [isDismissed, setIsDismissed] = useState(
    () => localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true',
  )

  if (isDismissed || isStandalone() || !isIosSafari()) return null

  function handleDismiss(): void {
    localStorage.setItem(DISMISSED_STORAGE_KEY, 'true')
    setIsDismissed(true)
  }

  return (
    <div
      role="region"
      aria-label="Install myTrellis"
      /* 4rem clears BottomNav's rendered height; keep in sync with AppShell.tsx's main
       * padding and install-prompt-banner.tsx's offset above. */
      className="bg-card fixed inset-x-4 bottom-[calc(4rem+1rem+env(safe-area-inset-bottom))] z-[45] flex items-start gap-3 rounded-xl border p-4 shadow-lg sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-96"
    >
      <img src="/icons/icon-192.png" alt="" className="size-10 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1">
        <Typography variant="body-sm" className="font-medium">
          Install myTrellis
        </Typography>
        <Typography
          variant="caption"
          color="muted"
          className="mt-1 inline-flex items-center gap-1"
        >
          Tap <Share className="size-3.5" aria-hidden="true" /> then "Add to Home Screen"
        </Typography>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="size-6 shrink-0"
        aria-label="Dismiss install instructions"
        onClick={handleDismiss}
      >
        <X className="size-3.5" />
      </Button>
    </div>
  )
}
